import { adminRecords } from "./adminRecords";
import React, { useEffect, useRef, useState } from "react";
import Auth from "../Auth";
import Select from "react-select";
import { FEATURE_GATE_OPTIONS } from "../shared/featureGates";
import GateSummary from "./GateSummary";

const Gates = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadedFeature, setLoadedFeature] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [copyNotice, setCopyNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [message, setMessage] = useState("");
  const [featureField, setFeatureField] = useState("plugins");
  const [saving, setSaving] = useState(false);
  const refreshId = useRef(0);
  const selectedGate = FEATURE_GATE_OPTIONS.find((option) => option.value === featureField);
  const oneClick = selectedGate?.oneClick;
  const isEnrollmentGate = !!oneClick;
  const enrollmentApi = oneClick?.adminApi;
  const enrollmentName = oneClick?.accessName;
  const ready = !loading && !error && loadedFeature === featureField;
  const shownSubscriptions = isEnrollmentGate && statusFilter !== "all"
    ? subscriptions.filter((subscription) => subscription.status === statusFilter)
    : subscriptions;
  const shownEmails = ready ? [...new Set(shownSubscriptions.map(({ email }) => email?.trim().toLowerCase()).filter(Boolean))] : [];

  const selectFilter = (value) => {
    setStatusFilter(value);
    setCopyNotice("");
  };

  const selectFeature = (value) => {
    selectFilter("all");
    setFeatureField(value);
  };

  useEffect(() => {
    refreshTable();
    const refresh = refreshId;
    return () => { refresh.current++; };
  }, [featureField]);

  const handleEmailChange = (event) => {
    setEmailInput(event.target.value);
  };

  const showUpdateSummary = ({ toggledList, noToggleList, notFoundList, failedList = [] }) => {
    const outcome = failedList.length ? "Subscriptions updated with errors." : "Subscriptions updated successfully.";
    const messageParts = [`${outcome} ${isEnrollmentGate ? "Invited" : "Toggled"}: ${toggledList.length}, No Toggle Required: ${noToggleList.length}, Not Found: ${notFoundList.length}`];
    if (failedList.length > 0) messageParts[0] += `, Failed: ${failedList.length}`;

    if (toggledList.length > 0) {
      messageParts.push(` | ${isEnrollmentGate ? "Invited" : "Gained Access"}: ${toggledList.join(", ")}`);
    }
    if (noToggleList.length > 0) {
      messageParts.push(` | ${isEnrollmentGate ? "Already invited or had access" : "Already had access"}: ${noToggleList.join(", ")}`);
    }
    if (notFoundList.length > 0) {
      messageParts.push(` | Not Found: ${notFoundList.join(", ")}`);
    }
    if (failedList.length > 0) {
      messageParts.push(` | Failed; try again: ${failedList.join(", ")}`);
    }
    setMessage(messageParts.join("\n"));
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();

    if (isEnrollmentGate) {
      setSaving(true);
      setMessage("");
      try {
        const emails = emailInput.split("\n").map((email) => email.trim()).filter(Boolean);
        const result = await Auth.fetchWithIdToken(`${enrollmentApi}/invite`, { emails });
        if (!result.success) throw new Error(result.error || "Could not invite accounts.");
        const emailsWithStatus = (...statuses) => result.results.filter(({ status }) => statuses.includes(status)).map(({ email }) => email);
        showUpdateSummary({
          toggledList: emailsWithStatus("invited"),
          noToggleList: emailsWithStatus("alreadyInvited", "alreadyEnabled"),
          notFoundList: emailsWithStatus("notFound"),
          failedList: emailsWithStatus("failed"),
        });
        setEmailInput(result.results.filter(({ status }) => status === "failed" || status === "notFound").map(({ email }) => email).join("\n"));
        await refreshTable();
      } catch (error) {
        setMessage(error.message);
      } finally {
        setSaving(false);
      }
      return;
    }

    const emails = emailInput.split("\n").map((email) => email.trim());

    const notFoundList = [];
    const noToggleList = [];
    const toggledList = [];

    const updatePromises = emails.map((email) => {
      const subscriptionRef = adminRecords.collection("subscriptions").where("email", "==", email);

      return subscriptionRef
        .get()
        .then(async (querySnapshot) => {
          if (querySnapshot.empty) {
            console.log(`No subscription found for email: ${email}`);
            notFoundList.push(email);
            return;
          }

          for (const subscriptionDoc of querySnapshot.docs) {
            const subscriptionData = subscriptionDoc.data();
            const updateData = { ...subscriptionData };
            if (updateData.features && updateData.features[featureField]) {
              noToggleList.push(email);
              return;
            } else {
              updateData.features = { ...subscriptionData.features, [featureField]: true };
            }

            toggledList.push(email);
            await Auth.fetchWithIdToken(`/api/admin/user/${encodeURIComponent(subscriptionDoc.id)}/gates`, { features: { [featureField]: true } });
          }

          return true;
        })
        .catch((err) => {
          console.error(`Error updating subscription for email: ${email}`, err);
          throw err;
        });
    });

    Promise.all(updatePromises)
      .then(async () => {
        console.log("All subscriptions updated successfully");
        setEmailInput("");
        showUpdateSummary({ toggledList, noToggleList, notFoundList });

        // send emails for plugins
        if (featureField === "plugins" && toggledList.length > 0) {
          Auth.fetchWithIdToken("/api/admin/send-plugin-emails", {
            emails: toggledList,
          });
        }

        await refreshTable();
      })
      .catch((err) => {
        console.error("Error updating subscriptions:", err);
        setMessage(`Error updating subscriptions: ${err.message}`);
      });
  };

  const handleRemoveClick = async (id) => {
    if (isEnrollmentGate) {
      setSaving(true);
      try {
        const result = await Auth.fetchWithIdToken(`${enrollmentApi}/remove`, { userId: id });
        if (!result.success) throw new Error(result.error || "Could not remove access.");
        setMessage(`Invitation and ${enrollmentName} access removed.`);
        await refreshTable();
      } catch (error) {
        setMessage(error.message);
      } finally {
        setSaving(false);
      }
      return;
    }
    Auth.fetchWithIdToken(`/api/admin/user/${encodeURIComponent(id)}/gates`, { features: { [featureField]: false } })
      .then(() => {
        console.log("Subscription ${id} updated successfully");
        refreshTable();
      })
      .catch((err) => {
        console.error("Error updating subscription ${id}:", err);
        setMessage("Error updating subscription ${id}:", err.message);
      });
  };

  const refreshTable = async () => {
    const requestId = ++refreshId.current;
    setLoading(true);
    setError(null);
    setSummary(null);
    setLoadedFeature(null);
    selectFilter("all");
    if (isEnrollmentGate) {
      try {
        const result = await Auth.fetchWithIdToken(`${enrollmentApi}/list`);
        if (!result.success) throw new Error(result.error || "Could not load invitations.");
        if (requestId === refreshId.current) {
          setSubscriptions(result.accounts);
          setSummary(result.summary || null);
          setLoadedFeature(featureField);
        }
      } catch (error) {
        if (requestId === refreshId.current) setError(error.message);
      } finally {
        if (requestId === refreshId.current) setLoading(false);
      }
      return;
    }
    const subscriptionsRef = adminRecords.collection("subscriptions");
    const ref = subscriptionsRef.where("features." + featureField, "==", true);

    Promise.all([ref.get()])
      .then(([snap]) => {
        if (requestId !== refreshId.current) return;
        const combineResults = (querySnapshot) => querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        const subs = combineResults(snap);

        // Merge results and remove duplicates
        const subscriptions = [...new Map([...subs].map((item) => [item.id, item])).values()];

        setSubscriptions(subscriptions);
        setLoadedFeature(featureField);
        setLoading(false);
      })
      .catch((err) => {
        if (requestId !== refreshId.current) return;
        setError(err);
        setLoading(false);
      });
  };

  return (
    <>
      <h1>Feature Gates</h1>

      <h3>Feature</h3>
      <Select
        aria-label="Feature"
        value={selectedGate}
        options={FEATURE_GATE_OPTIONS}
        onChange={(selectedOption) => selectFeature(selectedOption.value)}
        isDisabled={saving}
        placeholder="Select Feature"
      />

      <br />
      <br />

      {loading && <p role="status">Loading subscriptions...</p>}
      {error && <p role="alert">Error: {error.message || error}</p>}
      {error && <button type="button" onClick={refreshTable}>Retry loading</button>}

      {isEnrollmentGate && <GateSummary
        summary={summary} loading={!error && !ready} error={error}
        filter={statusFilter} onFilter={selectFilter} emails={shownEmails}
        disabled={!ready || saving} copyNotice={copyNotice}
        onCopy={(success) => setCopyNotice(success ? `Copied ${shownEmails.length} ${shownEmails.length === 1 ? "email" : "emails"}.` : "Copy failed. Please try again.")}
      />}

      {isEnrollmentGate && <p>Add up to 200 existing accounts at a time. Invited users enable {enrollmentName} access by opening the outreach link. Access starts after enrollment. No email is sent here.</p>}
      {oneClick?.enrollmentLink && <p>Email button destination: <code>{`${window.location.origin}${oneClick.enrollmentLink}`}</code></p>}

      <form onSubmit={handleEmailSubmit}>
        <label>
          Paste in emails (one per line):
          <br />
        </label>
        <textarea
          aria-label="Account emails"
          disabled={saving || loading || !!error}
          value={emailInput}
          onChange={handleEmailChange}
          style={{ width: "400px", height: "200px" }}
        />
        <br />
        <button type="submit" disabled={saving || loading || !!error}>{isEnrollmentGate ? (saving ? "Saving…" : "Invite accounts") : "Update Subscriptions"}</button>
      </form>
      <br />
      {message && <p role="status" style={{ whiteSpace: "pre-line" }}>{message}</p>}
      <br />
      <br />

      {ready && (
        <>
          <p>{isEnrollmentGate ? `Showing ${shownSubscriptions.length} of ${subscriptions.length} accounts` : `Total user with access: ${subscriptions.length}`}</p>

          <table style={{ borderCollapse: "collapse", border: "1px solid #ccc" }}>
            <thead>
              <tr>
                <th style={{ padding: "10px" }}>Subscription ID</th>
                <th style={{ padding: "10px" }}>Email</th>
                {isEnrollmentGate && <th style={{ padding: "10px" }}>Status</th>}
                <th style={{ padding: "10px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isEnrollmentGate && shownSubscriptions.length === 0 && <tr><td colSpan={4} style={{ padding: "10px" }}>No matching accounts.</td></tr>}
              {shownSubscriptions.map((subscription) => (
                <tr key={subscription.id}>
                  <td style={{ padding: "10px", border: "1px solid #ccc" }}>{subscription.id}</td>
                  <td style={{ padding: "10px", border: "1px solid #ccc" }}>{subscription.email}</td>
                  {isEnrollmentGate && <td style={{ padding: "10px", border: "1px solid #ccc" }}>{subscription.status}</td>}
                  <td style={{ padding: "10px", border: "1px solid #ccc" }}>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => handleRemoveClick(subscription.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};

export default Gates;
